import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  RefreshCw, ClipboardList, PackageCheck, PackageX, CheckCircle2, AlertTriangle, Receipt, FileDown,
  Wrench,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  pending: 'bg-gray-100 text-gray-700',
  picked: 'bg-indigo-100 text-indigo-800',
  qa_pending: 'bg-purple-100 text-purple-800',
  qa_completed: 'bg-teal-100 text-teal-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
};

function PLStatusBadge({ status }) {
  return (
    <Badge className={STATUS_COLORS[status] || 'bg-gray-100 text-gray-700'}>
      {String(status || '').replace(/_/g, ' ').toUpperCase()}
    </Badge>
  );
}

function PickListItemsTable({ items }) {
  // Defensive: surface ONLY lines that have actual allocated stock.
  // Pending-procurement / zero-alloc rows must never appear on the pick
  // list page — the backend strips them too, this is belt-and-braces.
  const allocOf = (ln) => Number(ln.alloc_qty ?? ln.allocated_qty ?? 0);
  const allocated = (items || []).filter((ln) => allocOf(ln) > 0);
  if (allocated.length === 0) {
    return (
      <div className="text-xs text-slate-500 italic py-2 px-3 border rounded bg-slate-50">
        No allocated stock on this pick list yet — waiting on procurement.
      </div>
    );
  }
  return (
    <div className="border rounded text-xs">
      <table className="w-full">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-2 py-1 text-left">Product</th>
            <th className="px-2 py-1 text-center">Alloc Qty</th>
            <th className="px-2 py-1 text-left">Rack</th>
            <th className="px-2 py-1 text-left">Bin</th>
            <th className="px-2 py-1 text-left">QA</th>
          </tr>
        </thead>
        <tbody>
          {allocated.map(line => (
            <tr key={line._uid || line.order_item_uid} className="border-t">
              <td className="px-2 py-1">
                {line.product_name}
                <span className="text-slate-400 ml-1">{line.product_sku}</span>
              </td>
              <td className="px-2 py-1 text-center font-semibold text-emerald-700">{allocOf(line)}</td>
              <td className="px-2 py-1">{line.rack_location || '—'}</td>
              <td className="px-2 py-1">{line.bin || '—'}</td>
              <td className="px-2 py-1"><PLStatusBadge status={line.qa_status || 'pending'} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PickListCard({ pickList, onMarkPicked, onOpenQA, onApprove, onDownloadPdf }) {
  const status = pickList.status;
  return (
    <Card>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">{pickList.pick_list_number}</h3>
              <PLStatusBadge status={status} />
              {pickList.order_number && (
                <span
                  className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border bg-blue-50 text-blue-800 border-blue-200 font-mono"
                  data-testid={`pl-order-${pickList.pick_list_number}`}
                >
                  {pickList.order_number}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-600">
              {pickList.franchise_name}
              <span className="text-slate-400"> ({pickList.franchise_business_id})</span>
            </p>
            <p className="text-xs text-slate-500">
              {(pickList.items || []).length} item(s) · created {new Date(pickList.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onDownloadPdf(pickList)}
              data-testid={`download-pdf-${pickList.pick_list_number}`} title="Print-ready slip with barcodes">
              <FileDown className="w-4 h-4 mr-1" />PDF
            </Button>
            {status === 'pending' && (
              <Button size="sm" onClick={() => onMarkPicked(pickList)}
                data-testid={`mark-picked-${pickList.pick_list_number}`}>
                <PackageCheck className="w-4 h-4 mr-1" />Mark Picked
              </Button>
            )}
            {status === 'qa_pending' && (
              <Button size="sm" variant="outline" onClick={() => onOpenQA(pickList)}
                data-testid={`open-qa-${pickList.pick_list_number}`}>
                <AlertTriangle className="w-4 h-4 mr-1" />QA Check
              </Button>
            )}
            {status === 'qa_completed' && (
              <Button size="sm" onClick={() => onApprove(pickList)}
                data-testid={`approve-${pickList.pick_list_number}`}>
                <CheckCircle2 className="w-4 h-4 mr-1" />Approve → Invoice
              </Button>
            )}
            {status === 'approved' && pickList.invoice_number && (
              <Badge className="bg-green-100 text-green-800">
                <Receipt className="w-3 h-3 mr-1" />{pickList.invoice_number}
              </Badge>
            )}
          </div>
        </div>
        <PickListItemsTable items={pickList.items} />
      </CardContent>
    </Card>
  );
}

function QADecisionRow({ line, decision, onChange }) {
  const isReject = decision.decision === 'reject';
  const picked = line.picked_qty ?? line.alloc_qty ?? line.allocated_qty ?? 0;
  const verified = decision.verified_qty ?? picked;
  const qtyChanged = !isReject && parseFloat(verified) !== parseFloat(picked);
  return (
    <tr className="border-t align-top">
      <td className="px-2 py-2">
        {line.product_name}
        <span className="text-slate-400 text-xs ml-1">{line.product_sku}</span>
      </td>
      <td className="px-2 py-2 text-center">{picked}</td>
      <td className="px-2 py-2">
        <div className="flex gap-1">
          <Button size="sm" variant={isReject ? 'outline' : 'default'}
            onClick={() => onChange({ decision: 'approve' })}
            data-testid={`qa-approve-${line._uid}`}>
            <PackageCheck className="w-3 h-3 mr-1" />Approve
          </Button>
          <Button size="sm" variant={isReject ? 'destructive' : 'outline'}
            onClick={() => onChange({ decision: 'reject', rejected_qty: picked })}
            data-testid={`qa-reject-${line._uid}`}>
            <PackageX className="w-3 h-3 mr-1" />Reject
          </Button>
        </div>
      </td>
      <td className="px-2 py-2 text-center">
        {isReject ? (
          <Input type="number" min="0" max={picked} value={decision.rejected_qty ?? 0}
            onChange={e => onChange({ rejected_qty: parseFloat(e.target.value) || 0 })}
            className="w-20 h-8 text-center" />
        ) : (
          <div>
            <Input type="number" min="0.01" step="0.01" value={verified}
              onChange={e => onChange({ decision: 'approve', verified_qty: e.target.value === '' ? picked : parseFloat(e.target.value) })}
              className={`w-20 h-8 text-center ${qtyChanged ? 'border-blue-500 bg-blue-50' : ''}`}
              title="Verified Qty — editable"
              data-testid={`qa-verified-qty-${line._uid}`} />
            {qtyChanged && <span className="block text-[10px] text-blue-600 mt-0.5">was {picked}</span>}
          </div>
        )}
      </td>
      <td className="px-2 py-2">
        {isReject && (
          <select
            value={decision.reason || ''}
            onChange={e => onChange({ reason: e.target.value })}
            className="h-8 text-sm border border-slate-300 rounded px-2 bg-white w-full"
            data-testid={`qa-reason-${line._uid}`}
          >
            <option value="">Select reason…</option>
            <option value="Shortage">Shortage</option>
            <option value="Damage">Damage</option>
            <option value="Expiry">Expiry</option>
            <option value="Physical Stock Not Available">Physical Stock Not Available</option>
            <option value="Manually Billed">Manually Billed</option>
          </select>
        )}
        {!isReject && qtyChanged && (
          <select
            value={decision.reason || ''}
            onChange={e => onChange({ reason: e.target.value })}
            className="h-8 text-sm border border-blue-300 rounded px-2 bg-white w-full"
            data-testid={`qa-qty-reason-${line._uid}`}
          >
            <option value="">Remarks (required)…</option>
            <option value="Customer requested additional quantity">Customer requested additional quantity</option>
            <option value="Customer reduced quantity">Customer reduced quantity</option>
            <option value="Short stock">Short stock</option>
            <option value="Packing adjustment">Packing adjustment</option>
            <option value="Warehouse correction">Warehouse correction</option>
            <option value="Other">Other</option>
          </select>
        )}
      </td>
    </tr>
  );
}

function QADialog({ pickList, onClose }) {
  const [decisions, setDecisions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const items = pickList.items || [];

  const setDecision = (uid, patch) =>
    setDecisions(prev => ({ ...prev, [uid]: { ...prev[uid], item_uid: uid, ...patch } }));

  const submit = async () => {
    // Client-side guard: modified verified qty requires remarks.
    for (const line of items) {
      const d = decisions[line._uid];
      if (!d || d.decision === 'reject' || d.verified_qty == null) continue;
      const picked = line.picked_qty ?? line.alloc_qty ?? line.allocated_qty ?? 0;
      if (parseFloat(d.verified_qty) !== parseFloat(picked) && !(d.reason || '').trim()) {
        toast.error(`Remarks are required for ${line.product_name} — verified qty was modified.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const finalDecisions = items.map(line => {
        const d = decisions[line._uid];
        if (!d) return { item_uid: line._uid, decision: 'approve' };
        return d;
      });
      const tk = localStorage.getItem('token');
      const res = await axios.post(`${API}/orders/pick-list/${pickList.id}/qa-submit`,
        { decisions: finalDecisions },
        { headers: { Authorization: `Bearer ${tk}` } });
      const physMissing = res.data.physical_missing_lines || 0;
      const manuallyBilled = res.data.manually_billed_lines || 0;
      const rejected = res.data.rejected_lines || 0;
      const parts = [];
      if (rejected > 0) parts.push(`${rejected} rejected → replacement PO`);
      if (physMissing > 0) parts.push(`${physMissing} Physical Stock Not Available → Pending Procurement`);
      if (manuallyBilled > 0) parts.push(`${manuallyBilled} Manually Billed → line closed (inventory deducted)`);
      if (parts.length) {
        toast.warning(parts.join(' · '));
      } else {
        toast.success('QA completed');
      }
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail ? String(err.response.data.detail) : 'QA submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto" data-testid="qa-dialog">
        <DialogHeader>
          <DialogTitle>QA Check — {pickList.pick_list_number}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-3 mb-2">
          Approve or reject each SKU. Rejected quantities return to warehouse inventory and
          auto-trigger a replacement PO.
        </p>
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-2 py-2 text-left">Product</th>
              <th className="px-2 py-2 text-center">Picked</th>
              <th className="px-2 py-2 text-left">Decision</th>
              <th className="px-2 py-2 text-center">Verified / Reject Qty</th>
              <th className="px-2 py-2 text-left">Reason / Remarks</th>
            </tr>
          </thead>
          <tbody>
            {items.map(line => (
              <QADecisionRow
                key={line._uid}
                line={line}
                decision={decisions[line._uid] || {}}
                onChange={(patch) => setDecision(line._uid, patch)}
              />
            ))}
          </tbody>
        </table>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="qa-submit-btn">
            {submitting ? 'Submitting…' : 'Submit QA'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PickListsTab({ onRefresh }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [qaOpen, setQaOpen] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');

  const fetchLists = useCallback(async () => {
    setLoading(true);
    try {
      const tk = localStorage.getItem('token');
      const res = await axios.get(`${API}/orders/pick-list/list`,
        { headers: { Authorization: `Bearer ${tk}` } });
      setLists(res.data || []);
    } catch (err) {
      toast.error('Failed to load pick lists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  const generate = async () => {
    try {
      const tk = localStorage.getItem('token');
      const res = await axios.post(`${API}/orders/pick-list/generate`, {},
        { headers: { Authorization: `Bearer ${tk}` } });
      if ((res.data.created || []).length) {
        toast.success(`Generated ${res.data.created.length} pick list(s)`);
      } else {
        toast.info(res.data.message || 'No new pick lists to generate');
      }
      fetchLists();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error('Generate failed');
    }
  };

  const healOrphans = async () => {
    try {
      const tk = localStorage.getItem('token');
      const res = await axios.post(`${API}/orders/admin/heal-orphan-picklists`, {},
        { headers: { Authorization: `Bearer ${tk}` } });
      const { scanned_ready_lines = 0, healed_lines = 0 } = res.data || {};
      if (healed_lines > 0) {
        toast.success(`Fixed ${healed_lines} stuck line${healed_lines === 1 ? '' : 's'} (out of ${scanned_ready_lines} ready)`);
      } else {
        toast.info(`Nothing to fix — all ${scanned_ready_lines} ready line(s) are already on a pick list`);
      }
      fetchLists();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fix failed');
    }
  };

  const dedupePickLists = async () => {
    const ok = window.confirm(
      'Remove duplicate line items from every open pick list?\n\n' +
      'Pick lists that had the same SKU pushed 2-4 times by repeated GRN / ' +
      'reallocation sweeps (a legacy bug, now fixed at the writer) will be ' +
      'collapsed to one row per order line. Safe & idempotent.'
    );
    if (!ok) return;
    try {
      const tk = localStorage.getItem('token');
      const res = await axios.post(`${API}/orders/pick-list/admin/dedupe`, {},
        { headers: { Authorization: `Bearer ${tk}` } });
      const d = res.data || {};
      if ((d.duplicate_lines_removed || 0) > 0) {
        toast.success(
          `Cleaned ${d.pick_lists_cleaned} pick list(s) — ` +
          `${d.duplicate_lines_removed} duplicate line(s) removed ` +
          `(scanned ${d.scanned}).`
        );
      } else {
        toast.info(`No duplicates found across ${d.scanned} open pick list(s).`);
      }
      fetchLists();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'De-dup failed');
    }
  };

  const markPicked = async (pickList) => {
    try {
      const tk = localStorage.getItem('token');
      await axios.post(`${API}/orders/pick-list/${pickList.id}/mark-picked`, {},
        { headers: { Authorization: `Bearer ${tk}` } });
      toast.success('Moved to QA Pending');
      fetchLists();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const approve = async (pickList) => {
    try {
      const tk = localStorage.getItem('token');
      const res = await axios.post(`${API}/orders/pick-list/${pickList.id}/approve`, {},
        { headers: { Authorization: `Bearer ${tk}` } });
      if (res.data.fulfillment_type === 'delivery_challan') {
        if (res.data.dc_status === 'draft') {
          toast.success(`Draft Delivery Challan ${res.data.delivery_challan_number} created (\u20B9${res.data.grand_total}) — review pricing & finalize in the Delivery Challan module`, { duration: 8000 });
        } else {
          toast.success(`Delivery Challan ${res.data.delivery_challan_number} dispatched (\u20B9${res.data.grand_total}) — FICO franchise`);
        }
      } else {
        toast.success(`Invoice ${res.data.invoice_number} drafted (\u20B9${res.data.grand_total})`);
      }
      fetchLists();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Approve failed');
    }
  };

  const downloadPdf = async (pickList) => {
    try {
      const tk = localStorage.getItem('token');
      const res = await axios.get(`${API}/orders/pick-list/${pickList.id}/pdf`, {
        headers: { Authorization: `Bearer ${tk}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pickList.pick_list_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${pickList.pick_list_number}.pdf downloaded`);
    } catch (err) {
      toast.error('PDF download failed');
    }
  };

  const closeQA = () => {
    setQaOpen(null);
    fetchLists();
    if (onRefresh) onRefresh();
  };

  // Apply search + status filter in memory.
  const filtered = lists.filter((pl) => {
    // Status filter
    if (statusFilter === 'open') {
      if (!['pending', 'in_progress'].includes(pl.status)) return false;
    } else if (statusFilter !== 'all') {
      if (pl.status !== statusFilter) return false;
    }
    // Search filter (franchise name / id / pick list number / order number).
    if (search) {
      const s = search.toLowerCase();
      const haystack = [
        pl.pick_list_number || '',
        pl.franchise_name || '',
        pl.franchise_business_id || '',
        pl.franchise_id || '',
        pl.order_number || '',
      ].join(' ').toLowerCase();
      if (!haystack.includes(s)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-3" data-testid="pick-lists-tab">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <p className="text-sm text-slate-600">
          {filtered.length} of {lists.length} pick list(s)
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchLists}>
            <RefreshCw className="w-4 h-4 mr-1" />Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={healOrphans}
            data-testid="heal-orphans-btn"
            title="Re-attach any Ready-to-Pick order line that lost its pick list (one-click cleanup)"
          >
            <Wrench className="w-4 h-4 mr-1" />Fix Missing Pick Lists
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={dedupePickLists}
            data-testid="dedupe-picklists-btn"
            title="Collapse duplicate SKU rows in pick lists (legacy bug — same line pushed 2-4 times by repeated sweeps)"
            className="text-amber-700 hover:bg-amber-50"
          >
            <Wrench className="w-4 h-4 mr-1" />De-dup Lines
          </Button>
          <Button size="sm" onClick={generate} data-testid="generate-pick-list-btn">
            <ClipboardList className="w-4 h-4 mr-1" />Generate Pick Lists
          </Button>
        </div>
      </div>
      {/* Search + status filter */}
      <div className="flex gap-3 items-center p-3 bg-slate-50 border rounded-lg flex-wrap">
        <Input
          placeholder="Search by franchise, franchise ID, pick list #, or order # …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 max-w-md flex-1 min-w-[260px]"
          data-testid="pick-list-search"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 px-2 text-sm border rounded bg-white"
          data-testid="pick-list-status-filter"
        >
          <option value="open">Open (Pending + In progress)</option>
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="qa_pending">QA Pending</option>
          <option value="qa_completed">QA Completed</option>
          <option value="approved">Approved</option>
        </select>
        {(search || statusFilter !== 'open') && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setSearch(''); setStatusFilter('open'); }}
            data-testid="pick-list-clear-filters"
          >
            Clear
          </Button>
        )}
      </div>
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {!loading && filtered.length === 0 && (
        <p className="text-sm text-slate-500 py-8 text-center border rounded-lg">
          {lists.length === 0
            ? 'No pick lists yet. Generate from Orders that are "ready for pick".'
            : `No pick lists match "${search || statusFilter}". Try clearing filters.`}
        </p>
      )}
      {filtered.map(pickList => (
        <PickListCard
          key={pickList.id}
          pickList={pickList}
          onMarkPicked={markPicked}
          onOpenQA={setQaOpen}
          onApprove={approve}
          onDownloadPdf={downloadPdf}
        />
      ))}
      {qaOpen && (
        <QADialog
          pickList={qaOpen}
          onClose={closeQA}
        />
      )}
    </div>
  );
}
